import React, { useState } from 'react';
import { ChevronDown, Check, X, Eye, Zap, FileText, Image, Table, Brain, ArrowRight, Calendar, RefreshCw, ClipboardList } from 'lucide-react';

export const AGENT_THUMBS = {
  luc: '/Thumbnails/Luc.png',
  marsi: '/Thumbnails/Marsi.png',
  sally: '/Thumbnails/Sally.png',
  buzz: '/Thumbnails/Buzz.png',
  wright: '/Thumbnails/Wright.png',
  zira: '/Thumbnails/Ziira.png',
  finn: '/Thumbnails/Finn.png',
  dora: '/Thumbnails/Dora.png',
  stark: '/Thumbnails/Stark.png',
  tali: '/Thumbnails/Tali.png',
};

const URGENCY_STYLES = {
  'time-sensitive': { label: 'Time Sensitive', dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-700 bg-amber-700/10' },
  'due-today': { label: 'Due Today', dark: 'text-orange-400 bg-orange-400/10', light: 'text-orange-700 bg-orange-700/10' },
  'critical': { label: 'Critical', dark: 'text-red-400 bg-red-400/10', light: 'text-red-600 bg-red-600/10' },
};

const ARTIFACT_ICONS = { pdf: FileText, image: Image, spreadsheet: Table };

export const SectionLabel = ({ icon: Icon, title, count, isDark, accent }) => {
  const accentColor = accent === 'red'
    ? (isDark ? 'text-red-400' : 'text-red-500')
    : accent === 'amber'
      ? (isDark ? 'text-amber-400' : 'text-amber-600')
      : accent === 'emerald'
        ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
        : (isDark ? 'text-white/50' : 'text-slate-500');
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className={`w-3.5 h-3.5 ${accentColor}`} />}
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${accentColor}`}>{title}</span>
      {count != null && (
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>{count}</span>
      )}
    </div>
  );
};

export const UrgencyBadge = ({ urgency, isDark }) => {
  const style = URGENCY_STYLES[urgency];
  if (!style) return null;
  return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? style.dark : style.light}`}>{style.label}</span>;
};

const ArtifactChip = ({ artifact, isDark }) => {
  const Icon = ARTIFACT_ICONS[artifact.type] || FileText;
  return (
    <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-white/[0.06] text-white/60 hover:bg-white/[0.1]' : 'bg-black/[0.04] text-slate-500 hover:bg-black/[0.08]'}`}>
      <Icon className="w-2.5 h-2.5" />
      {artifact.name}
    </button>
  );
};

const ThinkingTrail = ({ text, isDark }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} pt-2`}>
      <button onClick={() => setOpen(!open)} className={`flex items-center gap-1 text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`}>
        <Brain className="w-2.5 h-2.5" />
        {open ? 'Hide reasoning' : 'Why this?'}
        <ChevronDown className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className={`mt-1.5 text-[10px] leading-relaxed ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{text}</p>}
    </div>
  );
};

const ProposedActions = ({ actions, isDark }) => (
  <div className={`mt-2 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} pt-2`}>
    <p className={`text-[10px] font-medium mb-1.5 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>Proposed resolution</p>
    <div className="space-y-1">
      {actions.map((a, i) => (
        <div key={i} className={`flex items-center gap-1.5 text-[10px] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
          <ArrowRight className="w-2.5 h-2.5 flex-shrink-0" />
          <span>{a.label}</span>
          <span className={`${isDark ? 'text-white/30' : 'text-slate-500'}`}>— {a.agent}</span>
        </div>
      ))}
    </div>
  </div>
);

export const ExpandedDetails = ({ item, isDark }) => {
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';

  return (
    <div className="mt-3 ml-7 space-y-2">
      {item.expanded.summary && (
        <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.expanded.summary}</p>
      )}
      {item.expanded.details && (
        <div className={`rounded-md overflow-hidden border ${border}`}>
          {item.expanded.details.map((d, i) => (
            <div key={i} className={`flex items-center justify-between px-2.5 py-1.5 text-[10px] ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
              <span className={textSecondary}>{d.label}</span>
              <span className={`font-medium ${textPrimary}`}>{d.value}</span>
            </div>
          ))}
        </div>
      )}
      {item.expanded.artifacts && (
        <div>
          <p className={`text-[10px] font-medium mb-1.5 ${textSecondary}`}>Attachments</p>
          <div className="flex flex-wrap gap-1.5">
            {item.expanded.artifacts.map((a, i) => <ArtifactChip key={i} artifact={a} isDark={isDark} />)}
          </div>
        </div>
      )}
      {item.expanded.proposedActions && <ProposedActions actions={item.expanded.proposedActions} isDark={isDark} />}
      {item.expanded.thinking && <ThinkingTrail text={item.expanded.thinking} isDark={isDark} />}
    </div>
  );
};

export const CompactCard = ({ item, isDark, type }) => {
  const [expanded, setExpanded] = useState(false);
  const isRisk = type === 'risk';
  const cardBg = isRisk
    ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60')
    : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isRisk
    ? (isDark ? 'border-red-500/15' : 'border-red-200/60')
    : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';

  const agentThumb = AGENT_THUMBS[item.agent] || AGENT_THUMBS[item.agents?.[0]];
  const hasExpanded = item.expanded;

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`briefing-card-${item.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {item.urgency && <UrgencyBadge urgency={item.urgency} isDark={isDark} />}
        {item.severity === 'critical' && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Critical</span>}
        {item.severity === 'high' && <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-amber-400 bg-amber-400/10' : 'text-orange-700 bg-orange-700/10'}`}>High Risk</span>}
      </div>

      <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>

      {item.amount && (
        <p className={`text-[11px] font-medium mt-1 ml-7 ${textPrimary}`}>{item.type}: {item.amount}</p>
      )}

      <div className="flex items-center gap-1.5 mt-2 ml-7">
        {type === 'approval' && (
          <>
            <button className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-400 text-[10px] font-medium" data-testid={`approve-${item.id}`}>
              <Check className="w-2.5 h-2.5" /> Approve
            </button>
            <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary}`} data-testid={`reject-${item.id}`}>
              <X className="w-2.5 h-2.5" /> Reject
            </button>
          </>
        )}
        {type === 'risk' && item.expanded?.proposedActions && (
          <button className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 text-indigo-400 text-[10px] font-medium" data-testid={`resolve-${item.id}`}>
            <Zap className="w-2.5 h-2.5" /> Resolve
          </button>
        )}
        {hasExpanded && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
            data-testid={`review-${item.id}`}
          >
            <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>

      {expanded && hasExpanded && <ExpandedDetails item={item} isDark={isDark} />}
    </div>
  );
};

export const MeetingItem = ({ meeting, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const bg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const hasAgenda = meeting.agenda && meeting.agenda.length > 0;
  const isInternal = meeting.type === 'internal';

  return (
    <div className={`${bg} border ${border} rounded-lg px-3 py-2`} data-testid={`meeting-${meeting.id}`}>
      <div className="flex items-center gap-3">
        <div className={`text-[11px] font-mono font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'} w-16 flex-shrink-0`}>
          {meeting.time}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium truncate ${textPrimary}`}>{meeting.title}</p>
          <p className={`text-[10px] ${textSecondary}`}>{meeting.with} · {meeting.duration}</p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg} flex-shrink-0`}
          data-testid={`meeting-expand-${meeting.id}`}
        >
          <Eye className="w-2.5 h-2.5" />
          {expanded ? 'Less' : 'More'}
        </button>
      </div>

      {expanded && (
        <div className="mt-2 ml-[76px]">
          {hasAgenda ? (
            <div className="mb-2">
              <p className={`text-[10px] font-medium mb-1 ${textSecondary}`}>Agenda</p>
              <div className="space-y-1">
                {meeting.agenda.map((item, i) => (
                  <div key={i} className={`flex items-start gap-1.5 text-[11px] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                    <span className={`${textSecondary} flex-shrink-0`}>{i + 1}.</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <p className={`text-[11px] italic ${textSecondary}`}>No agenda set for this meeting</p>
              {isInternal && (
                <button
                  className="flex items-center gap-1 mt-1.5 px-2 py-1 rounded-md text-[10px] font-medium bg-indigo-500/20 text-indigo-400"
                  data-testid={`meeting-request-agenda-${meeting.id}`}
                >
                  <ClipboardList className="w-2.5 h-2.5" /> Request Agenda
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <button
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
              data-testid={`meeting-reschedule-${meeting.id}`}
            >
              <RefreshCw className="w-2.5 h-2.5" /> Reschedule
            </button>
            <button
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
              data-testid={`meeting-notes-${meeting.id}`}
            >
              <FileText className="w-2.5 h-2.5" /> Meeting Notes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ShouldDoItem = ({ item, isDark }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const agentThumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.01]'}`} data-testid={`should-do-${item.id}`}>
      {agentThumb && <img src={agentThumb} alt="" className="w-4 h-4 rounded-full object-cover" />}
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-medium truncate ${textPrimary}`}>{item.title}</p>
        <p className={`text-[10px] truncate ${textSecondary}`}>{item.description}</p>
      </div>
    </div>
  );
};


/* ═════════════════════════════════════════════════════════
   NeedsDecisionCard — Approve/Reject card used by dept leads
   (Marsi-driven for Priya, Sally-driven for David)
   ═════════════════════════════════════════════════════════ */
export const NeedsDecisionCard = ({ item, isDark, onApprove, onReject, decisionState }) => {
  const [expanded, setExpanded] = useState(false);
  const isHandled = !!decisionState;
  const cardBg = isHandled
    ? (decisionState === 'approved'
        ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
        : (isDark ? 'bg-slate-800/30' : 'bg-slate-100/80'))
    : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isHandled
    ? (decisionState === 'approved'
        ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60')
        : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'))
    : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`needs-decision-${item.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {isHandled ? (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${
            decisionState === 'approved'
              ? (isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10')
              : (isDark ? 'text-white/40 bg-white/[0.06]' : 'text-slate-500 bg-slate-200')
          }`}>
            {decisionState === 'approved' ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
            {decisionState === 'approved' ? 'Approved' : 'Rejected'}
          </span>
        ) : item.urgency === 'critical' ? (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Urgent</span>
        ) : null}
      </div>
      <p className={`text-[11px] mt-1 ml-7 leading-relaxed ${textSecondary}`}>{item.description}</p>
      {item.amount && (
        <p className={`text-[11px] font-medium mt-1 ml-7 ${textPrimary}`}>{item.amountLabel || 'Impact'}: {item.amount}</p>
      )}

      {!isHandled && (
        <div className="flex items-center gap-1.5 mt-2 ml-7">
          <button
            onClick={() => onApprove?.(item.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25'}`}
            data-testid={`decision-approve-${item.id}`}
          >
            <Check className="w-2.5 h-2.5" /> Approve
          </button>
          <button
            onClick={() => onReject?.(item.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
            data-testid={`decision-reject-${item.id}`}
          >
            <X className="w-2.5 h-2.5" /> Reject
          </button>
          {item.expanded && (
            <button
              onClick={() => setExpanded(!expanded)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
              data-testid={`decision-more-${item.id}`}
            >
              <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
            </button>
          )}
        </div>
      )}
      {expanded && item.expanded && <ExpandedDetails item={item} isDark={isDark} />}
    </div>
  );
};

export const InboxItem = ({ email, isDark, onReply }) => {
  const [expanded, setExpanded] = React.useState(false);
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2`} data-testid={`inbox-item-${email.id}`}>
      <div className="flex items-center gap-2">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold ${isDark ? 'bg-white/[0.08] text-white/70' : 'bg-black/[0.06] text-slate-600'}`}>
          {email.senderInitials}
        </div>
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{email.sender}</p>
        <span className={`text-[9px] whitespace-nowrap ${textSecondary}`}>{email.time}</span>
      </div>
      <p className={`text-[11px] mt-1 ml-7 truncate ${textSecondary}`}>{email.summary}</p>

      <div className="flex items-center gap-1.5 mt-1.5 ml-7">
        {onReply && (
          <button
            onClick={() => onReply(email.id)}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'} transition-colors`}
            data-testid={`inbox-reply-${email.id}`}
          >
            Reply
          </button>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
          data-testid={`inbox-more-${email.id}`}
        >
          {expanded ? 'Less' : 'More'}
        </button>
      </div>

      {expanded && (
        <div className={`mt-2 ml-7 p-2.5 rounded-md ${isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          <p className={`text-[11px] leading-relaxed ${textSecondary}`}>{email.expandedSummary}</p>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => onReply?.(email.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'} transition-colors`}
              data-testid={`inbox-reply-expanded-${email.id}`}
            >
              Reply
            </button>
            <button className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`} data-testid={`inbox-snooze-${email.id}`}>
              Snooze
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   RIPPLE UI — cross-account artifacts routed from another persona.
   Rendered merged into native lists (Tasks / Inbox / Approvals / Risks)
   with a compact "From {Source}" indigo pill.
   ═══════════════════════════════════════════════════════════ */

const SOURCE_LABEL = { arjun: 'Arjun', priya: 'Priya', david: 'David', maya: 'Maya', neel: 'Neel', riya: 'Riya' };

export const FromSourcePill = ({ source, isDark }) => {
  const label = SOURCE_LABEL[source] || source;
  return (
    <span
      className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'text-indigo-300 bg-indigo-400/10 border border-indigo-400/20' : 'text-indigo-700 bg-indigo-500/10 border border-indigo-300/40'}`}
      data-testid={`ripple-from-${source}`}
    >
      From {label}
    </span>
  );
};

/* Rippled Task Card — looks like FollowUpTask but with a FromSourcePill + subtle indigo highlight */
export const RippledTaskCard = ({ task, isDark, onComplete }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isDone = task.status === 'completed';
  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15 border-emerald-500/20' : 'bg-emerald-50/50 border-emerald-200/60')
    : (isDark ? 'bg-indigo-950/20 border-indigo-500/25' : 'bg-indigo-50/60 border-indigo-300/60');
  return (
    <div
      className={`border rounded-lg px-3 py-2.5 ${cardBg}`}
      style={{ animation: 'taskSlideIn 0.4s ease-out' }}
      data-testid={`ripple-task-${task.id}`}
    >
      <div className="flex items-center gap-2">
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{task.title}</p>
        <FromSourcePill source={task.from} isDark={isDark} />
      </div>
      {task.description && <p className={`text-[11px] mt-1 ${textSecondary}`}>{task.description}</p>}
      <div className="flex items-center gap-2 mt-2">
        {task.urgency && <UrgencyBadge urgency={task.urgency} isDark={isDark} />}
        {isDone ? (
          <span className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
            <Check className="w-2.5 h-2.5" /> Completed · {SOURCE_LABEL[task.from]} notified
          </span>
        ) : (
          <button
            onClick={() => onComplete?.(task.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`}
            data-testid={`ripple-task-complete-${task.id}`}
          >
            <Check className="w-2.5 h-2.5" /> Mark done
          </button>
        )}
      </div>
    </div>
  );
};

export const RippledInboxItem = ({ email, isDark, onHandle }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isDone = email.status === 'handled' || email.status === 'completed';
  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15 border-emerald-500/20' : 'bg-emerald-50/50 border-emerald-200/60')
    : (isDark ? 'bg-indigo-950/20 border-indigo-500/25' : 'bg-indigo-50/60 border-indigo-300/60');
  return (
    <div className={`border rounded-lg px-3 py-2 ${cardBg}`} data-testid={`ripple-email-${email.id}`}>
      <div className="flex items-center gap-2">
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{email.subject || email.title}</p>
        <FromSourcePill source={email.from} isDark={isDark} />
      </div>
      {email.summary && <p className={`text-[11px] mt-1 ${textSecondary}`}>{email.summary}</p>}
      {!isDone && onHandle && (
        <button
          onClick={() => onHandle(email.id)}
          className={`mt-2 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`}
          data-testid={`ripple-email-handle-${email.id}`}
        >
          <Check className="w-2.5 h-2.5" /> Mark read
        </button>
      )}
      {isDone && (
        <span className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
          <Check className="w-2.5 h-2.5" /> Read · {SOURCE_LABEL[email.from]} notified
        </span>
      )}
    </div>
  );
};

export const RippledApprovalCard = ({ item, isDark, onApprove, onReject }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isDone = item.status === 'approved' || item.status === 'rejected' || item.status === 'completed';
  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15 border-emerald-500/20' : 'bg-emerald-50/50 border-emerald-200/60')
    : (isDark ? 'bg-indigo-950/20 border-indigo-500/25' : 'bg-indigo-50/60 border-indigo-300/60');
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${cardBg}`} data-testid={`ripple-approval-${item.id}`}>
      <div className="flex items-center gap-2">
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        <FromSourcePill source={item.from} isDark={isDark} />
      </div>
      {item.description && <p className={`text-[11px] mt-1 ${textSecondary}`}>{item.description}</p>}
      {!isDone ? (
        <div className="flex gap-1.5 mt-2">
          <button
            onClick={() => onApprove?.(item.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'}`}
            data-testid={`ripple-approve-${item.id}`}
          >
            <Check className="w-2.5 h-2.5" /> Approve
          </button>
          <button
            onClick={() => onReject?.(item.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25' : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'}`}
            data-testid={`ripple-reject-${item.id}`}
          >
            <X className="w-2.5 h-2.5" /> Reject
          </button>
        </div>
      ) : (
        <span className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
          <Check className="w-2.5 h-2.5" /> {item.status === 'rejected' ? 'Rejected' : 'Approved'} · {SOURCE_LABEL[item.from]} notified
        </span>
      )}
    </div>
  );
};

export const RippledRiskCard = ({ item, isDark, onResolve }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isDone = item.status === 'resolved' || item.status === 'completed';
  const cardBg = isDone
    ? (isDark ? 'bg-emerald-950/15 border-emerald-500/20' : 'bg-emerald-50/50 border-emerald-200/60')
    : (isDark ? 'bg-indigo-950/20 border-indigo-500/25' : 'bg-indigo-50/60 border-indigo-300/60');
  return (
    <div className={`border rounded-lg px-3 py-2.5 ${cardBg}`} data-testid={`ripple-risk-${item.id}`}>
      <div className="flex items-center gap-2">
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        <FromSourcePill source={item.from} isDark={isDark} />
      </div>
      {item.description && <p className={`text-[11px] mt-1 ${textSecondary}`}>{item.description}</p>}
      {!isDone && onResolve && (
        <button
          onClick={() => onResolve(item.id)}
          className={`mt-2 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`}
          data-testid={`ripple-risk-resolve-${item.id}`}
        >
          <Check className="w-2.5 h-2.5" /> Acknowledge
        </button>
      )}
      {isDone && (
        <span className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
          <Check className="w-2.5 h-2.5" /> Acknowledged · {SOURCE_LABEL[item.from]} notified
        </span>
      )}
    </div>
  );
};
